import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import axios from "axios";
import { FadeIn } from "@/components/Reveal";
import { ShieldCheck, ArrowUpRight, Sparkles } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const WHATSAPP_NUMBER = "917004810637";

export default function DayOutingConfirmed() {
  const { id } = useParams();
  const [outing, setOuting] = useState(null);
  const [err, setErr] = useState("");

  useEffect(() => {
    axios.get(`${API}/day-outings/${id}`)
      .then(({ data }) => setOuting(data))
      .catch(() => setErr("Booking not found. Please check the link or write to us."));
  }, [id]);

  if (err) return (
    <main className="pt-32 pb-24 min-h-screen">
      <section className="mx-auto max-w-[900px] px-6 lg:px-12 text-center">
        <div className="stamp mb-6">Vol · X · Day Outing</div>
        <h1 className="font-display text-4xl text-[#F2E7D0]">{err}</h1>
      </section>
    </main>
  );

  if (!outing) return (
    <main className="pt-32 pb-24 min-h-screen">
      <section className="mx-auto max-w-[900px] px-6 lg:px-12 text-center">
        <div className="font-mono-label text-[#B8A891]">Loading your booking…</div>
      </section>
    </main>
  );

  const bookingShort = (outing.id || "").substring(0, 8).toUpperCase();
  const waMsg =
    `Hi Tikratoli! My Day Outing is confirmed.%0A%0A` +
    `Booking ID: ${bookingShort}%0A` +
    `Name: ${outing.full_name}%0A` +
    `Date: ${outing.date}%0A` +
    `Guests: ${outing.guests}%0A` +
    `Arrival: ${outing.arrival_time}`;

  return (
    <main data-testid="day-outing-confirmed" className="pt-32 pb-24">
      <section className="mx-auto max-w-[900px] px-6 lg:px-12">
        <FadeIn>
          <div className="stamp mb-6">Vol · X · Day Outing</div>
          <h1 className="font-display text-5xl md:text-7xl leading-[0.95] text-[#F2E7D0]">
            Confirmed — <em className="italic text-[#D97645]">see you soon</em>.
          </h1>
          <p className="mt-6 text-[#B8A891] text-lg">
            Your 1-Day Farm Outing is booked. Show your Booking ID at reception to avail your privileges.
          </p>

          {/* Summary */}
          <div className="mt-10 grid grid-cols-1 md:grid-cols-2 gap-6 border-t border-white/10 pt-8">
            <div><div className="chapter-num">Booking ID</div><div className="mt-2 font-display text-2xl text-[#F2E7D0]">{bookingShort}</div></div>
            <div><div className="chapter-num">Guest</div><div className="mt-2 text-[#F2E7D0]">{outing.full_name}</div></div>
            <div><div className="chapter-num">Date</div><div className="mt-2 text-[#F2E7D0]">{outing.date}</div></div>
            <div><div className="chapter-num">Arrival</div><div className="mt-2 text-[#F2E7D0]">{outing.arrival_time}</div></div>
            <div><div className="chapter-num">Guests</div><div className="mt-2 text-[#F2E7D0]">{outing.guests}</div></div>
            <div><div className="chapter-num">Total paid (incl. GST)</div><div className="mt-2 text-[#F2E7D0]">₹ {(outing.amount_paid_inr || outing.total_inr || 0).toLocaleString("en-IN")}</div></div>
          </div>

          {/* Club Membership perks */}
          <div className="mt-8 border border-[#D97645]/40 bg-[#27221C] p-6 md:p-8" data-testid="confirmed-privileges">
            <div className="flex items-center gap-2 font-mono-label text-[11px] tracking-[0.28em] text-[#D97645] mb-3">
              <Sparkles size={14} /> BECOME A TIKRATOLI CLUB MEMBER FOR A DAY
            </div>
            <ul className="grid grid-cols-1 md:grid-cols-3 gap-3 text-[#F2E7D0]">
              <li>• <strong>15% Off</strong> Food &amp; Beverages</li>
              <li>• <strong>10% Off</strong> Jeep Rides &amp; ATV</li>
              <li>• <strong>10% Off</strong> Indoor Games</li>
            </ul>
            <div className="mt-4 flex items-start gap-2 text-[11px] text-[#B8A891]">
              <ShieldCheck size={14} className="text-[#D97645] mt-0.5 shrink-0" />
              <span>Show your Booking ID <strong className="text-[#F2E7D0]">{bookingShort}</strong> at reception to avail. T&amp;C apply.</span>
            </div>
          </div>

          {/* WhatsApp */}
          <div className="mt-8 flex flex-wrap gap-4">
            <a
              href={`https://wa.me/${WHATSAPP_NUMBER}?text=${waMsg}`}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="confirmed-whatsapp"
              className="inline-flex items-center gap-3 bg-[#25D366] hover:bg-[#1EA952] transition text-[#1C1913] px-6 py-4 font-mono-label"
            >
              Share on WhatsApp <ArrowUpRight size={16} />
            </a>
            <Link
              to="/"
              className="inline-flex items-center gap-3 border border-white/20 hover:border-white/40 transition text-[#F2E7D0] px-6 py-4 font-mono-label"
            >
              Back to home
            </Link>
          </div>

          {/* Tagline */}
          <p className="mt-10 text-center font-display text-xl md:text-2xl font-bold tracking-[0.3em] text-[#F2E7D0]">
            RELAX. DINE. PLAY. EXPLORE.
          </p>
          <p className="mt-5 text-[11px] text-[#B8A891] leading-relaxed max-w-xl mx-auto text-center">
            <span className="font-mono-label tracking-[0.2em] text-[#D97645]">T&amp;C APPLY · </span>
            Discounts are subject to facility availability and applicable resort policies. Discounts cannot be clubbed with any other ongoing offer or promotion.
          </p>
        </FadeIn>
      </section>
    </main>
  );
}
